import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { initialMapDatasetIds } from '../constants/general';
import { useLayerData } from '../hooks/map/useLayerData';
import { useLayerManager } from '../hooks/map/useLayerManager';
import { usePointLayerData } from '../hooks/map/usePointLayerData';
import {
  // useLazyGetBleDepDataQuery,
  useLazyGetBoundaryDataQuery,
  useLazyGetFlowlinesDataQuery,
  useLazyGetHUC08DataQuery,
  useLazyGetHUC10DataQuery,
  useLazyGetHUC12DataQuery,
  useLazyGetRainGaugeQuery,
  useLazyGetStructureDataQuery,
  useLazyGetSurfaceWaterGaugesQuery,
} from '../services/api';
import { filterSelector } from '../store/selectors/filters';
import { CopyPasteCitation } from './CopyPasteCitation';
import { FloodMap } from './map/Map';
import { MapSidebar } from './MapSidebar';

export const FloodExplorer = ({ mapRef }) => {
  const [activeTab, setActiveTab] = useState('map');
  const layerManager = useLayerManager();
  const filterInfo = useSelector(filterSelector);
  const [tileData, setTileData] = useState({
    oneHundredYearFloodLayerTile: false,
    fiveHundredYearFloodLayerTile: false,
    existingFloodExposureLayerTile: false,
    futureFloodExposureLayerTile: false,
    bleDep100: false,
    bleDep500: false,
    wse100: false,
    wse500: false,
    waterbodiesTile: false,
    terrainContour: false,
    ble100FloodZoneData: false,
    ble500FloodZoneData: false,
    nfhl100FloodZoneData: false,
    nfhl500FloodZoneData: false,
    structureFootprintsData: false,
    contours2ftData: false,
    damagePlain: false
  });

  const [getBoundaryData, { data: boundaryData, isFetching: isBoundaryDataFetching }] =
    useLazyGetBoundaryDataQuery();

  const [
    getHUC08Data,
    { data: huc08Data, isFetching: isHUC08DataFetching, error: huc08DataError },
  ] = useLazyGetHUC08DataQuery();

  const [
    getHUC10Data,
    { data: huc10Data, isFetching: isHUC10DataFetching, error: huc10DataError },
  ] = useLazyGetHUC10DataQuery();

  const [
    getHUC12Data,
    { data: huc12Data, isFetching: isHUC12DataFetching, error: huc12DataError },
  ] = useLazyGetHUC12DataQuery();

  const [
    getStructureData,
    { data: structureData, isFetching: isStructureDataFetching, error: structureDataError },
  ] = useLazyGetStructureDataQuery();

  const [
    getFlowlinesData,
    { data: flowlinesData, isFetching: isFlowlinesDataFetching, error: flowlinesDataError },
  ] = useLazyGetFlowlinesDataQuery();

  const [
    getSurfaceWaterGaugesData,
    {
      data: surfaceWaterGaugesData,
      isFetching: isSurfaceWaterGaugesDataFetching,
      error: surfaceWaterGaugesDataError,
    },
  ] = useLazyGetSurfaceWaterGaugesQuery();

  const [
    getRainGaugeData,
    { data: rainGaugeData, isFetching: isRainGaugeDataFetching, error: rainGaugeDataError },
  ] = useLazyGetRainGaugeQuery();

  // const [
  //   getWaterbodiesData,
  //   { data: waterbodiesData, isFetching: isWaterbodiesDataFetching, error: waterbodiesDataError },
  // ] = useLazyGetWaterbodiesDataQuery();

  // Layer data hooks - connect API data to layer manager

  useLayerData({
    layerId: 'huc08',
    data: huc08Data,
    isLoading: isHUC08DataFetching,
    error: huc08DataError,
    layerManager,
  });

  useLayerData({
    layerId: 'huc10',
    data: huc10Data,
    isLoading: isHUC10DataFetching,
    error: huc10DataError,
    layerManager,
  });

  useLayerData({
    layerId: 'huc12',
    data: huc12Data,
    isLoading: isHUC12DataFetching,
    error: huc12DataError,
    layerManager,
  });

  useLayerData({
    layerId: 'flowlines',
    data: flowlinesData,
    isLoading: isFlowlinesDataFetching,
    error: flowlinesDataError,
    layerManager,
  });

  useLayerData({
    layerId: 'surfaceWaterGauges',
    data: surfaceWaterGaugesData,
    isLoading: isSurfaceWaterGaugesDataFetching,
    error: surfaceWaterGaugesDataError,
    layerManager,
  });

  useLayerData({
    layerId: 'rainGauges',
    data: rainGaugeData,
    isLoading: isRainGaugeDataFetching,
    error: rainGaugeDataError,
    layerManager,
  });
  // useLayerData({
  //   layerId: 'waterbodies',
  //   data: waterbodiesData,
  //   isLoading: isWaterbodiesDataFetching,
  //   error: waterbodiesDataError,
  //   layerManager,
  // });

  // This is synthetic data being passed to the map so map acknowledges data exists, but all vector data is being loaded from the map component based on visibility (which is set in the LayerSelectionSidebar component)
  useLayerData({
    layerId: 'oneHundredYearFloodLayerTile',
    data: tileData.oneHundredYearFloodLayerTile,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'fiveHundredYearFloodLayerTile',
    data: tileData.fiveHundredYearFloodLayerTile,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'existingFloodExposureLayerTile',
    data: tileData.existingFloodExposureLayerTile,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'futureFloodExposureLayerTile',
    data: tileData.futureFloodExposureLayerTile,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'bleDep100',
    data: tileData.bleDep100,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'bleDep500',
    data: tileData.bleDep500,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'wse100',
    data: tileData.wse100,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'wse500',
    data: tileData.wse500,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'terrainContour',
    data: tileData.terrainContour,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'ble100FloodZoneTile',
    data: tileData.ble100FloodZoneDataTile,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'ble500FloodZoneTile',
    data: tileData.ble500FloodZoneDataTile,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'nfhl100FloodZoneTile',
    data: tileData.nfhl100FloodZoneDataTile,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'nfhl500FloodZoneTile',
    data: tileData.nfhl500FloodZoneDataTile,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'structureFootprints',
    data: tileData.structureFootprintsData,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'contours2ftTile',
    data: tileData.contours2ftData,
    isLoading: false,
    error: false,
    layerManager,
  });

  useLayerData({
    layerId: 'damagePlain',
    data: tileData.damagePlain,
    isLoading: false,
    error: false,
    layerManager,
  })

  // useLayerData({
  //   layerId: 'waterbodiesTile',
  //   data: tileData.waterbodiesTile,
  //   isLoading: false,
  //   error: false,
  //   layerManager,
  // });

  // Fetch data based on filter changes
  useEffect(() => {
    // Only fetch data when we have valid filter information
    if (filterInfo.selectedCounty) {
      getBoundaryData(filterInfo);
      getStructureData(filterInfo);
      getHUC08Data(filterInfo);
      getHUC10Data(filterInfo);
      getHUC12Data(filterInfo);
      getFlowlinesData(filterInfo);
      getSurfaceWaterGaugesData(filterInfo);
      getRainGaugeData(filterInfo);
      //TODO ask if we want to include waterbodies - taking forever to load
      // getWaterbodiesData(filterInfo);
      setTileData(prevState => ({
        ...prevState,
        oneHundredYearFloodLayerTile: true,
        fiveHundredYearFloodLayerTile: true,
        bleDep100: true,
        bleDep500: true,
        wse100: true,
        wse500: true,
        terrainContour: true,
        ble100FloodZoneDataTile: true,
        ble500FloodZoneDataTile: true,
        nfhl100FloodZoneDataTile: true,
        nfhl500FloodZoneDataTile: true,
        structureFootprintsData: true,
        contours2ftData: true,
        damagePlain: true,
        // waterbodiesTile: true,
      }));
    }
    // eslint-disable-next-line
  }, [filterInfo]);

  const { availableLayers } = usePointLayerData({
    ids: initialMapDatasetIds,
    data: structureData,
    isLoading: isStructureDataFetching,
    error: structureDataError,
    layerManager,
  });

  return (
    <div className="rounded bg-iceGrey p-4">
      <div className="flex flex-col gap-4 rounded bg-white p-3 lg:grid lg:grid-cols-[400px_1fr] lg:items-stretch">
        <div className="w-full overflow-y-auto lg:h-0 lg:min-h-full">
          <MapSidebar
            layerManager={layerManager}
            structureData={structureData}
            availableLayers={availableLayers}
            setActiveTab={setActiveTab}
            activeTab={activeTab}
          />
        </div>

        <div className="flex flex-col gap-4 rounded">
          <FloodMap
            ref={mapRef}
            boundaryData={boundaryData}
            isBoundaryDataFetching={isBoundaryDataFetching}
            layerManager={layerManager}
            activeTab={activeTab}
          />
          <CopyPasteCitation />
        </div>
      </div>
    </div>
  );
};
